import { MarketTimeSeriesBreakdown } from "../../../../legacy/types/companySearch/MI";
import { TechnologyMarketShareItem } from "../MarketLandscape/charts/KeyPlayerMarketPenetrationBySegment/types";
import { SegmentProductDetection } from "../MarketLandscape/hooks/useProductDetectionsBySegment";

/**
 * Filters and sorts market share data to return the top 5 competitors by penetration percentage
 * @param marketShareData The technology market share data from the query
 * @returns Filtered and sorted market share data with only top 5 competitors per segment
 */
export function getTop5CompetitorsByPenetration(
  marketShareData: TechnologyMarketShareItem[] = []
): TechnologyMarketShareItem[] {
  return marketShareData.map((item) => {
    const filteredInstalls = item.installs
      .filter(
        (install) =>
          install.id != null &&
          install.penetrationPercent > 0 &&
          Number.isFinite(install.penetrationPercent) &&
          install.companyCount > 0 &&
          Number.isFinite(install.companyCount)
      )
      .sort((a, b) => b.penetrationPercent - a.penetrationPercent)
      .slice(0, 5);

    return {
      ...item,
      installs: filteredInstalls,
    };
  });
}

/**
 * Get the top 5 products by average hits
 */
export function getTop5Products<
  T extends
    | (MarketTimeSeriesBreakdown & { productId: number })
    | SegmentProductDetection,
>(data: T[]): T[] {
  // Track both total hits and count of appearances for each product
  const productHits: Record<number, number> = {};
  const productCounts: Record<number, number> = {};

  for (const item of data) {
    const { productId } = item;
    productHits[productId] = (productHits[productId] || 0) + item.hits;
    productCounts[productId] = (productCounts[productId] || 0) + 1;
  }

  const productAverages = Object.keys(productHits).map((stringId) => {
    const id = Number(stringId);
    const totalHits = productHits[id];
    const count = productCounts[id];
    const average = totalHits / count;
    return [id, average];
  });

  const sortedByAverage = [...productAverages].sort((a, b) => b[1] - a[1]);

  const top5ProductIds = new Set(
    sortedByAverage.slice(0, 5).map((entry) => entry[0])
  );

  return data.filter((item) => top5ProductIds.has(item.productId));
}
